import React from "react";
import {
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  DeleteOutlined as DeleteIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  ViewColumn as ViewColumnIcon,
  Calculate as CalculateIcon,
  AutoAwesome as AutoAwesomeIcon,
} from "@mui/icons-material";

export const PRESET_SCHEMAS = {
  material_service: {
    label: "Material / Service (Default)",
    description: "Standard requisition grid with auto-calculated total cost",
    columns: [
      { key: "description", label: "Description", type: "text" },
      { key: "scope", label: "Specification / Scope", type: "text" },
      { key: "quantity", label: "Quantity", type: "number" },
      { key: "unit_cost", label: "Estimated Unit Cost", type: "number" },
      {
        key: "total_cost",
        label: "Estimated Total Cost",
        type: "number",
        calculated: true,
        multiply: ["quantity", "unit_cost"],
      },
    ],
  },
  simple_inventory: {
    label: "Inventory / Items List",
    description: "Item Name, Unit, and Quantity list",
    columns: [
      { key: "item_name", label: "Item Name / Code", type: "text" },
      { key: "description", label: "Description / Specs", type: "text" },
      { key: "unit", label: "Unit (pcs, kg, etc.)", type: "text" },
      { key: "quantity", label: "Quantity", type: "number" },
    ],
  },
  services_hours: {
    label: "Professional Services / Hours",
    description: "Task description, hours, hourly rate, and subtotal",
    columns: [
      { key: "task_title", label: "Task / Deliverable", type: "text" },
      { key: "assignee", label: "Resource / Role", type: "text" },
      { key: "hours", label: "Estimated Hours", type: "number" },
      { key: "rate", label: "Hourly Rate (INR)", type: "number" },
      {
        key: "subtotal",
        label: "Subtotal",
        type: "number",
        calculated: true,
        multiply: ["hours", "rate"],
      },
    ],
  },
};

export const sanitizeKey = (label, fallbackIndex) => {
  if (!label) return `col_${fallbackIndex + 1}`;
  return (
    label
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9_]/g, "_")
      .replace(/^_+|_+$/g, "") || `col_${fallbackIndex + 1}`
  );
};

export default function GridColumnConfigurator({
  columns = [],
  onChange,
  isDark: propDark,
}) {
  const theme = useTheme();
  const isDark = propDark !== undefined ? propDark : theme.palette.mode === "dark";

  // Normalize columns
  const safeColumns =
    Array.isArray(columns) && columns.length > 0
      ? columns
      : PRESET_SCHEMAS.material_service.columns;

  const handleUpdateColumn = (index, updates) => {
    const updated = [...safeColumns];
    const target = { ...updated[index], ...updates };

    // Auto update key if label changed and key was derived
    if (updates.label && (!target.key || target.key.startsWith("col_"))) {
      target.key = sanitizeKey(updates.label, index);
    }

    // If turned into calculated, default multiply to previous numeric columns if any
    if (updates.calculated === true && (!target.multiply || target.multiply.length === 0)) {
      const numericCols = updated.filter(
        (c, i) => i !== index && (c.type === "number" || c.key.includes("qty") || c.key.includes("cost"))
      );
      if (numericCols.length >= 2) {
        target.multiply = [numericCols[0].key, numericCols[1].key];
      }
    }

    updated[index] = target;
    onChange(updated);
  };

  const handleAddColumn = () => {
    const newIdx = safeColumns.length;
    const newCol = {
      key: `col_${newIdx + 1}`,
      label: `Column ${newIdx + 1}`,
      type: "text",
      calculated: false,
    };
    onChange([...safeColumns, newCol]);
  };

  const handleRemoveColumn = (index) => {
    if (safeColumns.length <= 1) return;
    const filtered = safeColumns.filter((_, i) => i !== index);
    onChange(filtered);
  };

  const handleMoveColumn = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= safeColumns.length) return;
    const updated = [...safeColumns];
    const [moved] = updated.splice(index, 1);
    updated.splice(newIndex, 0, moved);
    onChange(updated);
  };

  const handleApplyPreset = (presetKey) => {
    if (PRESET_SCHEMAS[presetKey]) {
      onChange(JSON.parse(JSON.stringify(PRESET_SCHEMAS[presetKey].columns)));
    }
  };

  // Potential operand options for calculated columns
  const numericColumns = safeColumns.filter(
    (c) => c.type === "number" && !c.calculated
  );

  return (
    <Box
      sx={{
        mt: 1.5,
        p: 2,
        borderRadius: "10px",
        bgcolor: isDark ? "rgba(30, 41, 59, 0.4)" : "#f8fafc",
        border: `1.5px dashed ${isDark ? "#475569" : "#cbd5e1"}`,
      }}
    >
      {/* Header Bar */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <ViewColumnIcon sx={{ color: "#6366f1", fontSize: 20 }} />
          <Typography
            variant="subtitle2"
            fontWeight={800}
            sx={{ color: isDark ? "#c7d2fe" : "#312e81" }}
          >
            Custom Table Grid Columns ({safeColumns.length})
          </Typography>
        </Stack>

        {/* Quick Presets Dropdown */}
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
            Presets:
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleApplyPreset("material_service")}
            sx={{
              fontSize: "0.7rem",
              py: 0.2,
              px: 1,
              borderRadius: "6px",
              textTransform: "none",
            }}
          >
            Material/Service
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleApplyPreset("simple_inventory")}
            sx={{
              fontSize: "0.7rem",
              py: 0.2,
              px: 1,
              borderRadius: "6px",
              textTransform: "none",
            }}
          >
            Inventory
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleApplyPreset("services_hours")}
            sx={{
              fontSize: "0.7rem",
              py: 0.2,
              px: 1,
              borderRadius: "6px",
              textTransform: "none",
            }}
          >
            Services/Rate
          </Button>
        </Stack>
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
        Configure the exact columns your company requires for this grid. Sl. No. is automatically added as the first column.
      </Typography>

      {/* Columns List */}
      <Stack spacing={1.5}>
        {safeColumns.map((col, idx) => {
          const isCalculated = Boolean(col.calculated);

          return (
            <Paper
              key={idx}
              elevation={0}
              sx={{
                p: 1.5,
                borderRadius: "8px",
                border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
                bgcolor: isDark ? "rgba(15, 23, 42, 0.6)" : "#ffffff",
              }}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                alignItems={{ xs: "stretch", md: "center" }}
              >
                {/* Column Order / Reorder */}
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Chip
                    label={`#${idx + 1}`}
                    size="small"
                    sx={{
                      fontWeight: 800,
                      fontSize: "0.7rem",
                      bgcolor: isDark ? "rgba(99, 102, 241, 0.2)" : "#e0e7ff",
                      color: "#4f46e5",
                      width: 32,
                    }}
                  />
                  <IconButton
                    size="small"
                    disabled={idx === 0}
                    onClick={() => handleMoveColumn(idx, -1)}
                    sx={{ p: 0.4 }}
                  >
                    <ArrowUpIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    disabled={idx === safeColumns.length - 1}
                    onClick={() => handleMoveColumn(idx, 1)}
                    sx={{ p: 0.4 }}
                  >
                    <ArrowDownIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Stack>

                {/* Column Header Title */}
                <TextField
                  size="small"
                  label="Column Header Title"
                  value={col.label || ""}
                  onChange={(e) => handleUpdateColumn(idx, { label: e.target.value })}
                  placeholder="e.g. Unit Price, Specification"
                  sx={{ flex: 2 }}
                />

                {/* Column Data Type */}
                <FormControl size="small" sx={{ width: { xs: "100%", md: 150 } }}>
                  <InputLabel>Data Type</InputLabel>
                  <Select
                    label="Data Type"
                    value={col.type || "text"}
                    onChange={(e) => {
                      const newType = e.target.value;
                      handleUpdateColumn(idx, {
                        type: newType,
                        calculated: newType === "calculated" ? true : col.calculated,
                      });
                    }}
                  >
                    <MenuItem value="text">Text</MenuItem>
                    <MenuItem value="number">Number</MenuItem>
                    <MenuItem value="date">Date</MenuItem>
                    <MenuItem value="calculated">Calculated Formula</MenuItem>
                  </Select>
                </FormControl>

                {/* Calculation Config (if type is calculated) */}
                {isCalculated && (
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{
                      p: 0.75,
                      borderRadius: "6px",
                      bgcolor: isDark ? "rgba(99, 102, 241, 0.15)" : "#eff6ff",
                      border: "1px solid rgba(99, 102, 241, 0.3)",
                      flex: 2,
                    }}
                  >
                    <CalculateIcon sx={{ fontSize: 18, color: "#6366f1" }} />
                    <Typography variant="caption" fontWeight={700} color="#4f46e5">
                      Auto =
                    </Typography>

                    <Select
                      size="small"
                      value={col.multiply?.[0] || ""}
                      onChange={(e) =>
                        handleUpdateColumn(idx, {
                          multiply: [e.target.value, col.multiply?.[1] || ""],
                        })
                      }
                      displayEmpty
                      sx={{ fontSize: "0.75rem", height: 32, minWidth: 90 }}
                    >
                      <MenuItem value="" disabled>
                        <em>Col 1</em>
                      </MenuItem>
                      {numericColumns.map((nc) => (
                        <MenuItem key={nc.key} value={nc.key} sx={{ fontSize: "0.75rem" }}>
                          {nc.label || nc.key}
                        </MenuItem>
                      ))}
                    </Select>

                    <Typography variant="caption" fontWeight={800}>
                      ×
                    </Typography>

                    <Select
                      size="small"
                      value={col.multiply?.[1] || ""}
                      onChange={(e) =>
                        handleUpdateColumn(idx, {
                          multiply: [col.multiply?.[0] || "", e.target.value],
                        })
                      }
                      displayEmpty
                      sx={{ fontSize: "0.75rem", height: 32, minWidth: 90 }}
                    >
                      <MenuItem value="" disabled>
                        <em>Col 2</em>
                      </MenuItem>
                      {numericColumns.map((nc) => (
                        <MenuItem key={nc.key} value={nc.key} sx={{ fontSize: "0.75rem" }}>
                          {nc.label || nc.key}
                        </MenuItem>
                      ))}
                    </Select>
                  </Stack>
                )}

                {/* Actions: Delete Column */}
                <Tooltip title={safeColumns.length <= 1 ? "At least 1 column is required" : "Remove column"}>
                  <span>
                    <IconButton
                      size="small"
                      color="error"
                      disabled={safeColumns.length <= 1}
                      onClick={() => handleRemoveColumn(idx)}
                      sx={{ p: 0.8 }}
                    >
                      <DeleteIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Paper>
          );
        })}
      </Stack>

      {/* Add Column Button */}
      <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
        <Button
          size="small"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddColumn}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 700,
            fontSize: "0.78rem",
            px: 2,
          }}
        >
          Add Grid Column
        </Button>
      </Box>
    </Box>
  );
}
